<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import { computed, ref, watch } from 'vue';
    import SelectInput from '../../SelectInput.vue';
    import EchoButton from '../../EchoButton.vue';
    import IconButton from '../../IconButton.vue';
    import LoadingDots from '../../LoadingDots.vue';
    import { RoleShare } from '@/js/services/api/shareDashboard/types';
    import {
        useGetRoleSharesForDashboard,
        usePutShareDashboardToRoles,
    } from '@/js/services/api/shareDashboard/shareDashboardQueries';
    import { useQueryGetRoles } from '@/js/services/api/role/roleQueries';
    import { useToastStore } from '@/js/stores/snackbar';
    import { useDashboardStore } from '@/js/stores/dashboard';
    import { PlusIcon, XIcon } from '@lucide/vue';

    const props = defineProps<{
        dashboardId: string;
    }>();

    const selectedRoleId = ref<string>('');

    const { data: rolesResponse, isLoading: isLoadingRoles } = useQueryGetRoles();

    const allRoles = computed(() => rolesResponse.value?.roles ?? []);

    const { data: sharedRolesResponse, isLoading: isLoadingRoleShares } =
        useGetRoleSharesForDashboard(props.dashboardId);

    const toast = useToastStore();

    const { mutateAsync: saveShares, isPending: isSavingShares } = usePutShareDashboardToRoles(
        props.dashboardId
    );

    const shareWithRoles = ref<RoleShare[]>([]);

    const hasChanges = computed(() => {
        const server = sharedRolesResponse.value;
        if (!server) return false;
        if (server.length !== shareWithRoles.value.length) return true;
        const serverIds = new Set(server.map((r) => r.id));
        return shareWithRoles.value.some((r) => !serverIds.has(r.id));
    });

    const isRoleAdded = (roleId: string) => {
        const server = sharedRolesResponse.value;
        if (!server) return false;
        return !server.some((r) => r.id === roleId);
    };

    const roleOptions = computed(() => {
        return allRoles.value.filter(
            (role) => !shareWithRoles.value.some((shared) => shared.id === role.id)
        );
    });

    const canAddRole = computed(() => !!selectedRoleId.value);

    const dashboard = useDashboardStore();
    const isDashboardOwner = computed(() => dashboard.isDashboardOwner);

    const addRole = () => {
        if (!isDashboardOwner.value) return;

        const roleId = selectedRoleId.value;
        const details = allRoles.value.find((r) => r.id === roleId);

        if (!roleId || !details) {
            return;
        }

        shareWithRoles.value.unshift({
            id: roleId,
            name: details.name,
        });

        selectedRoleId.value = '';
    };

    const removeRole = (roleId: string) => {
        if (!isDashboardOwner.value) return;

        shareWithRoles.value = shareWithRoles.value.filter((role) => role.id !== roleId);
    };

    const save = async () => {
        if (!isDashboardOwner.value) return;

        const roleIds = shareWithRoles.value.map((role) => role.id);

        try {
            await saveShares(roleIds);
            toast.addToast('success', __('share-dashboard-roles-save-success'));
        } catch (error) {
            console.error('Error saving role shares:', error);
            toast.addToast('error', __('share-dashboard-roles-save-error'));
        }
    };

    defineExpose({ hasUnsavedChanges: hasChanges });

    watch(
        () => sharedRolesResponse.value,
        (sharesRolesFromDatabase) => {
            if (sharesRolesFromDatabase) {
                const sortedRoles = [...sharesRolesFromDatabase].sort((a, b) =>
                    a.name.localeCompare(b.name)
                );

                shareWithRoles.value = sortedRoles;
            }
        },
        { immediate: true }
    );
</script>

<template>
    <h2 class="heading-md-light">
        {{ __('add-roles') }}
    </h2>
    <p class="text-body">
        {{ __('share-with-role-description') }}
    </p>

    <form
        class="flex flex-col sm:flex-row gap-4 sm:items-center mt-5"
        autocomplete="off"
        @submit.prevent="addRole()"
    >
        <SelectInput
            v-model="selectedRoleId"
            :disabled="!isDashboardOwner || isLoadingRoles"
            size="full"
        >
            <option value="" disabled>{{ __('select-role') }}</option>
            <option v-for="role in roleOptions" :key="role.id" :value="role.id">
                {{ role.name }}
            </option>
        </SelectInput>
        <div>
            <EchoButton
                :disabled="!isDashboardOwner || !canAddRole"
                type="submit"
                variant="secondary"
            >
                {{ __('add') }}
                <template #icon-right><PlusIcon /></template>
            </EchoButton>
        </div>
    </form>

    <div class="flex-grow flex flex-col mt-6 relative">
        <div v-if="isLoadingRoleShares" class="py-10">
            <LoadingDots />
        </div>

        <template v-else>
            <div class="lg:h-0 flex-grow flex flex-col">
                <div class="border-b border-gray-light pb-1.5">
                    <h3 class="heading-md-light flex items-center">
                        <template v-if="hasChanges">
                            {{
                                __('will-be-shared-with-roles', {
                                    count: shareWithRoles.length.toString(),
                                })
                            }}
                        </template>
                        <template v-else>
                            {{
                                __('shared-with-roles', {
                                    count: shareWithRoles.length.toString(),
                                })
                            }}
                        </template>
                    </h3>
                </div>
                <div class="relative lg:h-0 flex-grow flex flex-col">
                    <div
                        v-if="isSavingShares"
                        class="absolute w-full h-full flex justify-center items-center z-10"
                    >
                        <LoadingDots />
                    </div>
                    <div class="overflow-auto custom-scrollbar">
                        <div
                            v-for="sharedRole in shareWithRoles"
                            :key="'role-' + sharedRole.id"
                            class="grid grid-cols-[1fr_auto_auto] items-center text-body odd:bg-gray-lightest py-2 transition-opacity"
                            :class="{ 'opacity-25 pointer-events-none': isSavingShares }"
                        >
                            <div class="pl-3">
                                <strong class="inline-block pr-3 text-body-bold relative">
                                    {{ sharedRole.name }}
                                    <div
                                        v-if="isRoleAdded(sharedRole.id)"
                                        class="bg-primary-carrot w-1.5 h-1.5 rounded-full absolute top-1.75 right-0"
                                    />
                                </strong>
                            </div>
                            <div class="pl-3 text-body">
                                {{ __('can-view') }}
                            </div>
                            <div class="px-3 text-error">
                                <IconButton
                                    area-size="size-5"
                                    :tooltip="__('remove')"
                                    :disabled="!isDashboardOwner"
                                    @click="removeRole(sharedRole.id)"
                                >
                                    <XIcon class="size-full" />
                                </IconButton>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="pt-5 border-t border-gray-light flex justify-end">
                    <EchoButton
                        variant="primary"
                        :loading="
                            isSavingShares || (isLoadingRoleShares && shareWithRoles.length > 0)
                        "
                        :disabled="!isDashboardOwner || !hasChanges"
                        @click="save()"
                    >
                        {{ __('save') }}
                    </EchoButton>
                </div>
            </div>
        </template>
    </div>
</template>
